import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Description as FileIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { portfolioService } from '../../services/portfolioService';

interface UploadDialogProps {
  open: boolean;
  onClose: () => void;
  portfolioId: number;
  onSuccess: () => void;
}

interface UploadResult {
  message: string;
  new_transactions: number;
  duplicate_skipped: number;
  replaced_positions: number;
  filename: string;
  total_parsed: number;
  file_type: 'open_positions' | 'transactions';
}

const UploadDialog: React.FC<UploadDialogProps> = ({
  open,
  onClose,
  portfolioId,
  onSuccess,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string>('');

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
      setUploadResult(null);
      setError('');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/html': ['.html', '.htm'],
    },
    multiple: false,
  });

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    setUploading(true);
    setError('');
    try {
      const result = await portfolioService.uploadTransactions(portfolioId, selectedFile);
      setUploadResult(result);
      
      if (result.file_type === 'open_positions') {
        if (result.replaced_positions > 0) {
          toast.success(`Successfully updated ${result.new_transactions} open positions (replaced ${result.replaced_positions} old positions)`);
        } else {
          toast.success(`Successfully imported ${result.new_transactions} open positions`);
        }
        onSuccess();
      } else if (result.new_transactions > 0) {
        toast.success(`Successfully imported ${result.new_transactions} transactions`);
      onSuccess();
      } else if (result.duplicate_skipped > 0) {
        toast.warning(`All ${result.duplicate_skipped} transactions were duplicates and skipped`);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to upload file';
      setError(errorMessage);
      
      if (err.response?.data?.duplicate_file) {
        toast.warning('This file has already been uploaded to this portfolio');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Upload Transaction File</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Upload a CSV or Excel file containing your transaction data. The file should have the following columns:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText 
                primary="Required columns:" 
                secondary="stock_symbol, stock_name, transaction_type (BUY/SELL), quantity, price, transaction_date"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Date format:" 
                secondary="YYYY-MM-DD (e.g., 2023-12-25)"
              />
            </ListItem>
          </List>
        </Box>

        {!uploadResult && (
          <Paper
            {...getRootProps()}
            sx={{
              p: 4,
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              cursor: 'pointer',
              textAlign: 'center',
              bgcolor: isDragActive ? 'action.hover' : 'background.paper',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            <input {...getInputProps()} />
            <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            {selectedFile ? (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Selected File:
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="center">
                  <FileIcon sx={{ mr: 1 }} />
                  <Typography>{selectedFile.name}</Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {isDragActive ? 'Drop the file here' : 'Drag & drop a file here, or click to select'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Supported formats: CSV, XLS, XLSX
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="subtitle1">Upload Failed</Typography>
            <Typography variant="body2">{error}</Typography>
          </Alert>
        )}

        {uploadResult && (
          <Alert severity={uploadResult.new_transactions > 0 ? "success" : "info"} sx={{ mt: 2 }}>
            <Box display="flex" alignItems="center">
              <SuccessIcon sx={{ mr: 1 }} />
              <Box>
                <Typography variant="subtitle1">
                  {uploadResult.file_type === 'open_positions' 
                    ? (uploadResult.replaced_positions > 0 ? 'Open Positions Updated!' : 'Open Positions Imported!')
                    : (uploadResult.new_transactions > 0 ? 'Upload Successful!' : 'Upload Completed')
                  }
                </Typography>
                <Typography variant="body2">
                  {uploadResult.message}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    📊 Total records parsed: {uploadResult.total_parsed}
                  </Typography>
                  <Typography variant="body2">
                    ✅ {uploadResult.file_type === 'open_positions' ? 'Open positions' : 'New transactions'} imported: {uploadResult.new_transactions}
                  </Typography>
                  {uploadResult.replaced_positions > 0 && (
                    <Typography variant="body2">
                      🔄 Old positions replaced: {uploadResult.replaced_positions}
                    </Typography>
                  )}
                  {uploadResult.duplicate_skipped > 0 && (
                <Typography variant="body2">
                      ⚠️ Duplicates skipped: {uploadResult.duplicate_skipped}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    File: {uploadResult.filename} ({uploadResult.file_type === 'open_positions' ? 'Open Positions' : 'Transaction History'})
                </Typography>
                </Box>
              </Box>
            </Box>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {uploadResult ? 'Close' : 'Cancel'}
        </Button>
        {!uploadResult && (
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UploadDialog; 